import { useCallback, useEffect, useState } from 'react';
import { clientAllocate } from '@fiverr-private/experiments';
import { getContext } from '@fiverr-private/fiverr_context';
import { AllocationGroups, Experiments } from '../../../shared/constants/experiments';
import { globalMixpanelReporter } from '../../../events';
import { useExperiment } from '../../../shared/hooks/useExperiment';
import { SELLER_COUNTRIES, BLOCK_COUNTRY_LIST } from '../../../shared/constants/countriesList';

interface UseIllustrationExperimentProps {
    inLynxesIdentificationIllustrationRollout: boolean;
    isModal: boolean;
    isModalOpen: boolean;
}

const { LYNXES_IDENTIFICATION_ILLUSTRATION_RIGHT, LYNXES_MODERN_HEADER } = Experiments;

const mixpanelEnrichment = (group: number) => {
    globalMixpanelReporter.enrichContext({ identificationIllustrationExperiment: group }, { preserve: true });
};

const getExperimentRestrictions = () => {
    const { url, countryCode } = getContext();

    const isHomepage = url ? new URL(url).pathname === '/' : false;
    const isBlockedCountry = BLOCK_COUNTRY_LIST.includes(countryCode);
    const isSellerCountry = SELLER_COUNTRIES.includes(countryCode);
    const { group: inModernHeaderExperimentGroup } = useExperiment(LYNXES_MODERN_HEADER);

    return isHomepage || isBlockedCountry || isSellerCountry || !!inModernHeaderExperimentGroup;
};

export const useIllustrationExperiment = ({
    inLynxesIdentificationIllustrationRollout,
    isModal,
    isModalOpen,
}: UseIllustrationExperimentProps) => {
    const { group } = useExperiment(LYNXES_IDENTIFICATION_ILLUSTRATION_RIGHT);

    const withExperimentRestrictions = getExperimentRestrictions();
    const canAllocate = isModal && isModalOpen && !withExperimentRestrictions;

    const [experimentGroup, setExperimentGroup] = useState(group);

    const clientIllustrationAllocation = useCallback(() => {
        const allocatedGroup = clientAllocate({
            experimentId: LYNXES_IDENTIFICATION_ILLUSTRATION_RIGHT,
            experimentType: 'guest',
            numOfGroups: 2,
            rolloutResult: inLynxesIdentificationIllustrationRollout,
        });

        if (allocatedGroup) {
            setExperimentGroup(allocatedGroup);
            mixpanelEnrichment(allocatedGroup);
        }
    }, [inLynxesIdentificationIllustrationRollout]);

    useEffect(() => {
        if (canAllocate && !experimentGroup) {
            clientIllustrationAllocation();
        }
    }, [canAllocate, experimentGroup, clientIllustrationAllocation]);

    return { inLynxesIdentificationIllustrationExperimentGroupB: experimentGroup === AllocationGroups.B };
};
